/** 每日链路重跑（E4 #5007）：`POST /chain/daily/run` 的请求体与 `GET /chain/daily` 的 `last_manual_run`。
 *
 * 五步怎么跑、停机时拒哪一步、断点是哪一步都由服务端判定（`someo_keel/api/chain.py`）；
 * 这里只拼请求体、解析回显、排一句话。
 */

import { apiUrl, expectRecord, optionalNumber, optionalString } from "../api/client";
import { ContractError } from "../api/errors";

export const CHAIN_RERUN_PATH = "/chain/daily/run";

/** 表单之外随请求带上的固定字段：重跑当前显示的交易日；续跑时 `from_step=auto`（断点由服务端取）。 */
export function chainRerunExtra(tradeDate: string, resume: boolean): Record<string, string> {
  return resume ? { trade_date: tradeDate, from_step: "auto" } : { trade_date: tradeDate };
}

export interface ChainRerunStep {
  step: string;
  label: string;
  status: string;
  detail: string;
}

export interface LastManualRun {
  ts_ns: number | null;
  operator: string | null;
  reason: string | null;
  from_step: string | null;
  job_id: string | null;
  paper_run_refused: string | null;
  job_status: string | null;
  finished_utc: string | null;
  error: string | null;
  broken_step: string | null;
  steps: ChainRerunStep[];
}

/** `last_manual_run`：没有为 null；有则是对象，非对象即契约不符。 */
export function parseLastManualRun(value: unknown): LastManualRun | null {
  if (value === null || value === undefined) return null;
  const url = apiUrl("/chain/daily");
  const r = expectRecord(value, url, "最近一次手工重跑");
  const result = r["result"] && typeof r["result"] === "object" ? (r["result"] as Record<string, unknown>) : null;
  const rawSteps = result && Array.isArray(result["steps"]) ? (result["steps"] as unknown[]) : [];
  const steps = rawSteps.map((item): ChainRerunStep => {
    if (!item || typeof item !== "object") throw new ContractError("重跑结果的步不是对象", url, item);
    const s = item as Record<string, unknown>;
    return { step: String(s["step"] ?? ""), label: String(s["label"] ?? ""), status: String(s["status"] ?? ""), detail: String(s["detail"] ?? "") };
  });
  return {
    ts_ns: optionalNumber(r, "ts_ns"),
    operator: optionalString(r, "operator"),
    reason: optionalString(r, "reason"),
    from_step: optionalString(r, "from_step"),
    job_id: optionalString(r, "job_id"),
    paper_run_refused: optionalString(r, "paper_run_refused"),
    job_status: optionalString(r, "job_status"),
    finished_utc: optionalString(r, "finished_utc"),
    error: optionalString(r, "error"),
    broken_step: result ? optionalString(result, "broken_step") : null,
    steps,
  };
}

const STATUS_TEXT: Record<string, string> = { ok: "完成", failed: "失败", not_run: "未执行", skipped: "跳过", refused: "被拒" };

/** 一句话：谁、为什么、跑到哪、结果如何（结论取后端字段，不在前端重判）。 */
export function lastManualRunText(run: LastManualRun): string {
  const who = `${run.operator ?? "—"}（${run.reason ?? "—"}）`;
  const scope = run.from_step ? `从 ${run.from_step} 续跑` : "整轮重跑";
  if (run.job_status === null) return `${who} · ${scope} · 任务状态未知`;
  if (run.job_status === "running") return `${who} · ${scope} · 进行中`;
  if (run.job_status === "failed") return `${who} · ${scope} · 任务异常：${run.error ?? "—"}`;
  const steps = run.steps.map((s) => `${s.label}${STATUS_TEXT[s.status] ?? s.status}`).join(" → ");
  const verdict = run.broken_step ? `中断于「${run.broken_step}」` : "未中断";
  return `${who} · ${scope} · ${verdict}${steps ? ` · ${steps}` : ""}${run.paper_run_refused ? ` · ${run.paper_run_refused}` : ""}`;
}
